import {
  Action,
  AgendaWeek,
  AssistantConversation,
  AssistantStreamEvent,
  AssistantSuggestions,
  AssistantTip,
  ActionStakes,
  ErrorBody,
  WeeklyAnalysis,
  WeeklyAnalysisResponse,
  type AssistantChatBody,
  type CreateActionBody,
} from '@cockpit/shared';
import { z } from 'zod';
import { createSseParser } from '../assistant/sse';
import { ApiError, request } from './client';

/**
 * Client de l'assistant et du Conseiller (lot E2), à part de `api/index.ts` pour ne pas croiser les autres
 * lots. `POST /api/actions` et `GET /api/actions` servent aux pistes du Conseiller et au Mode écran.
 */
export const assistantApi = {
  suggestions: () => request('GET', '/api/assistant/suggestions', { schema: AssistantSuggestions }),
  conversation: (id: string) =>
    request('GET', `/api/assistant/conversations/${encodeURIComponent(id)}`, {
      schema: AssistantConversation,
    }),
  weekly: () => request('GET', '/api/assistant/weekly', { schema: WeeklyAnalysisResponse }),
  refreshWeekly: () => request('POST', '/api/assistant/weekly/refresh', { schema: WeeklyAnalysis }),
  tip: () => request('GET', '/api/assistant/tip', { schema: AssistantTip }),
  createTask: (body: CreateActionBody) => request('POST', '/api/actions', { body, schema: Action }),
  openActions: () => request('GET', '/api/actions?who=all&status=open', { schema: z.array(Action) }),
  stakes: () => request('GET', '/api/actions/stakes?who=all', { schema: ActionStakes }),
  agendaWeek: () => request('GET', '/api/agenda/week', { schema: AgendaWeek }),
  /** Lot AD : conversation liée à un retour (administrateur ou auteur). */
  feedbackConversation: (feedbackId: string) =>
    request('GET', `/api/feedback/${encodeURIComponent(feedbackId)}/conversation`, {
      schema: AssistantConversation,
    }),
};

/**
 * `POST /api/assistant/chat` : lit le flux SSE et rend chaque événement validé. Un refus avant le flux
 * (plafond atteint, conversation introuvable…) lève une ApiError comme les autres appels.
 */
export async function streamChat(
  body: AssistantChatBody,
  onEvent: (event: AssistantStreamEvent) => void,
  options: { signal?: AbortSignal; fetch?: typeof fetch } = {},
): Promise<void> {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  let response: Response;
  try {
    response = await doFetch('/api/assistant/chat', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', 'Le serveur ne répond pas. Vérifiez votre connexion.');
  }
  if (!response.ok || !response.body) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = undefined;
    }
    const parsed = ErrorBody.safeParse(data);
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      throw new ApiError(response.status, code, message, details);
    }
    throw new ApiError(
      response.status,
      'HTTP_ERROR',
      `Erreur inattendue du serveur (HTTP ${response.status}).`,
    );
  }
  const parser = createSseParser();
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  const handle = (messages: { data: string }[]) => {
    for (const m of messages) {
      let json: unknown;
      try {
        json = JSON.parse(m.data);
      } catch {
        continue;
      }
      const event = AssistantStreamEvent.safeParse(json);
      if (event.success) onEvent(event.data);
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    handle(parser.feed(value));
  }
  handle(parser.flush());
}
